<script>
import { apiMixins } from "../../../mixins/api.js";
import Popup from "../../common/Popup.vue";
import RetentionSchedulePicker from "../../common/RetentionSchedulePicker.vue";
import moment from "moment";

export default {
  props: {
    item: {
      type: Object,
      required: false,
    },
    displayRetainHistoric: {
      type: Boolean,
      required: false
    }
  },
  components: { Popup, RetentionSchedulePicker },
  mixins: [apiMixins],
  emits: ["retention-schedule-changed", "retain-historic-changed"],
  data() {
    return {
      availableCategories: [],
      allRetentionSchedules: [],
      availableRetentionSchedules: [],
      currentRetentionSchedule: null,
      selectedRetentionSchedule: null,
      newRetentionScheduleVisible: false,
      checkRetainHistoric: this.item && this.item.isRetainHistoric,
      parsedItemSchedule: this.item && this.item.defaultRetention && this.item.defaultRetention.replace("#", ""),
    };
  },
  asyncComputed: {},
  computed: {
    formattedRetentionSchedule() {
      if (this.checkRetainHistoric && this.isRetainHistoric()) {
        return this.$t("retention-schedule.keep-historic-title.past-tense");
      }
      if (this.checkRetainHistoric && !this.isRetainHistoric()) {
        return this.$t("retention-schedule.keep-historic-title.future-tense");
      }
      if (this.currentRetentionSchedule) {
        return `${this.currentRetentionSchedule.id} ${this.currentRetentionSchedule.category} ${this.currentRetentionSchedule.title} ${this.currentRetentionSchedule.period}`;
      }
      return this.$t("retention-schedule.no-retention-schedule");
    },
    selectedScheduleExpiryDriven() {
      return this.currentRetentionSchedule?.expiryDriven !== false
        && !this.currentRetentionSchedule?.supersededRetention;
    },
  },
  methods: {
    estimateExpiry() {
      let d = new Date();
      if (!this.currentRetentionSchedule) {
         return null;
      }
      if (this.currentRetentionSchedule.period.endsWith("D")) {
        return moment(d).add("days", this.currentRetentionSchedule.daysUntilExpiry).format("YYYY-MM-DD");
      } else if (this.currentRetentionSchedule.period.endsWith("M")) {
        return moment(d).add("months", this.currentRetentionSchedule.monthsUntilExpiry).format("YYYY-MM-DD");
      }
      return moment(d).add("years", this.currentRetentionSchedule.yearsUntilExpiry).format("YYYY-MM-DD");
    },
    async loadRetentionSchedules() {
      return this.callAPI("item/retention-schedules", { showLoader: false })
        .then((response) => {
          this.allRetentionSchedules = response.retentionSchedules;
          this.availableRetentionSchedules = this.allRetentionSchedules.filter((r) => r.isActive === true);
          const categories = this.availableRetentionSchedules
            .map((retentionSchedule) => retentionSchedule.category)
            .sort();
          const uniqueCategories = [...new Set(categories)];
          this.availableCategories = uniqueCategories;
        })
        .then(() => {
          if (this.isStoredItemRetentionScheduleEmpty()) {
            return;
          }
          this.currentRetentionSchedule = this.availableRetentionSchedules.find(
            (r) => r.id === this.parsedItemSchedule
          );
          this.$emit("retention-schedule-changed", this.currentRetentionSchedule);
        });
    },
    isAdmin() {
      return this.$store.state.auth.userData.isAdmin;
    },
    isExpired() {
      return this.item && this.item.isExpired;
    },
    isOwner() {
      return this.item && this.item.owner && this.item.owner.includesMe;
    },
    isSteward() {
      return this.item && this.item.steward && this.item.steward.includesMe;
    },
    isUploader() {
      return this.item && this.item.permissions && this.item.permissions.writeAccess;
    },
    isRetainHistoric() {
      return this.item && this.item.isRetainHistoric;
    },
    onNewRetentionScheduleHandler() {
      this.newRetentionScheduleVisible = true;
      this.selectedRetentionSchedule = null;
    },
    onNewRetentionScheduleCancelledHandler() {
      this.newRetentionScheduleVisible = false;
      this.selectedRetentionSchedule = null;
    },
    onNewRetentionScheduleConfirmedHandler() {
      this.newRetentionScheduleVisible = false;
      this.currentRetentionSchedule = this.selectedRetentionSchedule;
      this.$emit("retention-schedule-changed", this.currentRetentionSchedule);
    },
    onRetainHistoricChangedHandler() {
      this.$emit("retain-historic-changed", this.checkRetainHistoric);
    },
    onNewRetentionScheduleChangedHandler(selectedRetentionSchedule) {
      this.selectedRetentionSchedule = selectedRetentionSchedule;
    },
    isStoredItemRetentionScheduleEmpty() {
      return (
        !this.item || !this.item.defaultRetention || this.item.defaultRetention === "N/A"
      );
    },
    isNewRetentionScheduleAvailable() {
      return this.isSteward() || this.isOwner() || this.isAdmin();
    },
    isRetainHistoricAvailable() {
      return (
          this.isRetainHistoric() ||
          (!this.isRetainHistoric() && this.isExpired() && this.isSteward()) ||
          (!this.isRetainHistoric() && this.isExpired() && this.isOwner()) ||
          (!this.isRetainHistoric() && this.isAdmin())
        );
    },
    hasRetentionScheduleChanged() {
      if (!this.item && this.currentRetentionSchedule) {
        return true;
      }
      if (this.item && this.currentRetentionSchedule) {
        return this.currentRetentionSchedule.id !== this.parsedItemSchedule;
      }
      return false;
    },
    shouldShowNewRetentionSchedule() {
      return (
        (!this.item || this.isSteward() || this.isOwner() || this.isAdmin() || this.isUploader()) &&
        !this.checkRetainHistoric
      );
    },
    showRetainHistoric() {
      return (
          this.item && (
        this.isRetainHistoric() ||
        (!this.isRetainHistoric() && this.isExpired() && this.isSteward() && this.displayRetainHistoric) ||
        (!this.isRetainHistoric() && this.isExpired() && this.isOwner() && this.displayRetainHistoric) ||
        (!this.isRetainHistoric() && this.isAdmin() && this.displayRetainHistoric))
      );
    },
  },
  mounted() {
    this.loadRetentionSchedules();
  },
};
</script>

<template>
  <section class="expiry">
    <section>
      <h5>
        <span class="title-with-help">
          Retention schedule
          <span
            class="help-icon"
            :title="$t('retention-schedule.help')"
            :aria-label="$t('retention-schedule.help')"
          >?</span>
        </span>
      </h5>
      <div class="retention-layout">
        <div class="retention-content">
          <div class="retention-block">
            <strong>{{ this.formattedRetentionSchedule }}</strong>
          </div>
          <div class="retention-block" v-if="!currentRetentionSchedule">
            {{ $t("retention-schedule.keep-until.upload") }}
          </div>
          <div class="retention-block" v-if="showRetainHistoric()">
            <m-checkbox
              id="retain-historic-checkbox"
              v-model="checkRetainHistoric"
              style="padding-top:0rem;padding-left:0rem"
              @change="onRetainHistoricChangedHandler"
            />
            <label for="retain-historic-checkbox">{{
              $t("retention-schedule.keep-historic-label")
            }}</label>
          </div>

          <div class="warning" v-if="selectedScheduleExpiryDriven && hasRetentionScheduleChanged() && !checkRetainHistoric">
            <p>
              {{
                $t("retention-schedule.estimated-expiry.upload", {
                  expiry: estimateExpiry(),
                })
              }}
              {{ $t("retention-schedule.steward-warning") }}
            </p>
          </div>
        </div>
        <div class="retention-actions">
          <button
            class="primary"
            :title="$t('retention-schedule.action-tooltip')"
            :aria-label="$t('retention-schedule.action-tooltip')"
            @click="onNewRetentionScheduleHandler"
            :disabled="!shouldShowNewRetentionSchedule()"
          >
            <m-icon icon="event" />
          {{ currentRetentionSchedule ? "Change..." : "Set..." }}
          </button>
        </div>
      </div>
    </section>

    <Popup
      v-if="newRetentionScheduleVisible"
      class="retention-schedule-popup"
      @cancelled="onNewRetentionScheduleCancelledHandler"
      @confirmed="onNewRetentionScheduleConfirmedHandler"
      :defaultButtons="false"
    >
      <template v-slot:header>{{ $t("retention-schedule-picker.header") }}</template>
      <template v-slot:body>
        <RetentionSchedulePicker
          id="retention-schedule-picker"
          :item="item"
          :availableRetentionSchedules="availableRetentionSchedules"
          :currentRetentionSchedule="currentRetentionSchedule != null ? currentRetentionSchedule.id : null"
          @retention-schedule-changed="onNewRetentionScheduleChangedHandler"
        />
      </template>
      <template v-slot:footer>
          <button type="button" 
            @click.stop="onNewRetentionScheduleCancelledHandler">
            <slot name="cancelLabel">{{ $t("cancel") }}</slot>
          </button>
          <button class="primary" type="submit" 
          :disabled="!selectedRetentionSchedule && !hasRetentionScheduleChanged()" 
            @click.prevent="onNewRetentionScheduleConfirmedHandler">
            <slot name="confirmLabel">{{ $t("ok") }}</slot>
          </button>
      </template>
    </Popup>
  </section>
</template>

<style lang="scss" scoped>
h5 {
  font-weight: bold;
  margin: 0 0 1rem;
}

.title-with-help {
  align-items: center;
  display: inline-flex;
  gap: 0.35rem;
}

.help-icon {
  align-items: center;
  background: transparent;
  border: 1px solid #1275CC;
  border-radius: 50%;
  color: #1275CC;
  cursor: help;
  display: inline-flex;
  flex: 0 0 1.25rem;
  font-size: 0.75rem;
  font-weight: 700;
  height: 1.25rem;
  justify-content: center;
  line-height: 1;
  margin: 0;
  padding: 0;
  width: 1.25rem;
}

.expiry {
  display: flex;
  flex-direction: column;

  > section {
    flex: 1 1 auto;
    margin: 1rem 0;
    background: white;
    border: 1px solid #C8D2DC;
    border-radius: 8px;
    box-sizing: border-box;
    padding: 1.5rem;

    label {
      margin-left: 1rem;
      font-weight: bold;
    }

    input[type="checkbox"] {
      margin-right: 0.5rem;
    }

    button {
      //margin-left: 2rem;
      width: auto;
      background: transparent;
      border: 1px solid #1275cc;
      color: #1275cc;
      border-radius: 4px;
      padding: 0.75rem 1rem;
      font-weight: bold;
      cursor: pointer;
      line-height: 1.6rem;

      &:disabled {
        cursor: default;
        opacity: 0.2;
      }

      &.primary {
        background: #1275cc;
        border-color: transparent;
        color: white;
      }

      > i {
      vertical-align: -0.35rem;
      padding-right: 0.25rem;
    }
  }

  .retention-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) max-content;
    gap: 1rem;
    align-items: start;
    padding-left: 1rem;
  }

    .retention-block {
      margin-bottom: 1rem;
    }
  }

  .retention-actions {
    justify-self: end;

    > button {
      align-items: center;
      display: inline-flex;
      justify-content: center;
      width: 10.5rem;
    }
  }

  .warning {
    color: #ef0000;
    margin-top: 1rem;
  }

::v-deep .retention-schedule-popup {
  .mdc-dialog__surface {
    max-height: calc(100vh - 2rem);
  }

  form {
    max-height: calc(100vh - 2rem);
    min-height: 0;
  }

  section {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
  }

  footer {
    flex: 0 0 auto;
  }
}

  footer {
  padding: 0 1.5rem 1.5rem;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

footer > * {
  margin-left: 1.5rem;
}

  footer button {
  background: transparent;
  border: 1px solid #1275CC;
  color: #1275CC;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  font-weight: bold;
  cursor: pointer;
  line-height: 1.6rem;

  &:disabled {
    cursor: default;
    opacity: 0.2;
  }

  &.primary {
    background: #1275CC;
    border-color: transparent;
    color: white;
  }

  > i {
    vertical-align: -0.35rem;
    padding-right: 0.25rem;
  }
}
}
</style>
